import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import chunk from "lodash/chunk";
import classNames from "classnames";
import { Box, styled, useMediaQuery } from "@schaeffler-commerce/design-system";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import throttle from "lodash/throttle";
import { MEDIA_QUERY_MIN_TABLET } from "@schaeffler-commerce/design-system";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps<T extends { index: number }> {
  teasers: T[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  renderTeaserDetailsContainer: (validIds: number[]) => ReactNode;
  renderTeaser: (teaser: T) => ReactNode;
}

export function CMSTeaserContainer<T extends { index: number }>({
  scrollType: desktopScrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow: desktopItemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  className,
  renderTeaserDetailsContainer,
  renderTeaser,
}: CMSTeasersProps<T>) {
  const isDesktop = useIsMinDesktopSize();
  const prevRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLDivElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const teasersWithId = teasers.map((teaser, index) => ({ ...teaser, id: index }));

  const containerRef = useRef<HTMLDivElement>(null);
  const isTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const [maxTeasersPerRow, setMaxTeasersPerRow] = useState(isDesktop ? desktopItemsPerRow : mobileItemsPerRow);

  useEffect(() => {
    const fn = throttle(() => {
      if (isDesktop) {
        setMaxTeasersPerRow(desktopItemsPerRow);
      } else if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width === 0) {
          requestAnimationFrame(fn);
          return;
        }
        const calculatedTeasersPerRow = Math.floor(width / 228);
        setMaxTeasersPerRow(
          Math.max(calculatedTeasersPerRow > desktopItemsPerRow ? desktopItemsPerRow : calculatedTeasersPerRow, 1),
        );
      } else {
        setMaxTeasersPerRow((prev) => (prev && prev > 0 ? prev : 1));
      }
    }, 100);
    window.addEventListener("resize", fn);
    window.addEventListener("orientationchange", fn);
    fn();
    return () => {
      window.removeEventListener("resize", fn);
      window.removeEventListener("orientationchange", fn);
    };
  }, [isDesktop, isTablet, desktopItemsPerRow]);

  let itemStyle: CSSProperties | undefined = undefined;

  // Deprecated ?
  const scrollType: string = isDesktop ? desktopScrollType : mobileScrollType;

  if (scrollType !== MobileContainerTypes.HORIZONTAL) {
    itemStyle = {
      width: `calc((100% / ${maxTeasersPerRow})`,
    };
  }

  const useCarousel =
    (isDesktop && desktopScrollType === DesktopContainerTypes.CAROUSEL) ||
    (!isDesktop && mobileScrollType === MobileContainerTypes.CAROUSEL);

  const containerCls = `scha__teasers__elements scha__teasers__elements--${scrollType}`;
  const elementCls = `scha__teasers__element scha__teasers__element--${scrollType}`;

  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
    }
  }, [teasers]);

  useEffect(() => {
    if (!useCarousel || !swiperRef.current) {
      return;
    }

    const swiper = swiperRef.current;

    // Pagination
    if (paginationRef.current && swiper.params.pagination && typeof swiper.params.pagination !== "boolean") {
      swiper.params.pagination.el = paginationRef.current;

      swiper.pagination.init();
      swiper.pagination.render();
      swiper.pagination.update();
    }

    // Navigation
    if (
      prevRef.current &&
      nextRef.current &&
      swiper.params.navigation &&
      typeof swiper.params.navigation !== "boolean"
    ) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;

      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [useCarousel, maxTeasersPerRow, teasers]);

  let data;
  if (useCarousel) {
    const spaceBetween = isDesktop ? 24 : isTablet ? 16 : 8;
    data = (
      <Swiper
        modules={[Pagination, Navigation]}
        pagination={{
          el: paginationRef.current,
          clickable: true,
        }}
        navigation={true}
        onBeforeInit={(swiper) => {
          if (typeof swiper.params.navigation !== "boolean") {
            swiper.params.navigation!.prevEl = prevRef.current;
            swiper.params.navigation!.nextEl = nextRef.current;
          }
          if (typeof swiper.params.pagination !== "boolean") {
            swiper.params.pagination!.el = paginationRef.current;
          }
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView={maxTeasersPerRow}
        slidesPerGroup={maxTeasersPerRow}
        spaceBetween={spaceBetween}
        className={containerCls}
      >
        {teasersWithId.map((item) => (
          <SwiperSlide
            key={`CMSTeaser-${item.index}`}
            className={classNames(elementCls, "scha__teasers__swiper-slide")}
          >
            {renderTeaser(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  } else {
    let rows;
    if (scrollType === MobileContainerTypes.HORIZONTAL) {
      rows = [teasersWithId];
    } else {
      rows = chunk(teasersWithId, maxTeasersPerRow);
    }
    data = (
      <>
        {rows.map((row, containerIndex) => (
          <div className={"scha__teasers__group"} key={`scha__teasers__level--${containerIndex}`}>
            <ul className={containerCls}>
              {row.map((item) => (
                <li key={item.id} style={itemStyle} className={elementCls}>
                  {renderTeaser(item)}
                </li>
              ))}
            </ul>
            {renderTeaserDetailsContainer(row.map((t) => t.index))}
          </div>
        ))}
      </>
    );
  }

  if (!teasers.length) {
    return null;
  }

  const hasPages = Math.ceil(teasers.length / maxTeasersPerRow) > 1;

  return (
    <StyledTeaserContainer
      className={classNames(
        `scha__teasers scha__teasers--${scrollType}`,
        {
          "scha__teasers--isDesktop": isDesktop,
          "scha__teasers--isTablet": isTablet,
        },
        className,
      )}
      ref={containerRef}
    >
      {data}
      {useCarousel && hasPages && (
        <div className="swiper-pagination-wrapper">
          <div ref={prevRef} className="swiper-button swiper-button-prev">
            <ChevronLeftIcon color="primary" fontSize="medium" />
          </div>
          <div ref={paginationRef} className="swiper-pagination-bullet-wrapper" />
          <div ref={nextRef} className="swiper-button swiper-button-next">
            <ChevronRightIcon color="primary" fontSize="medium" />
          </div>
        </div>
      )}
    </StyledTeaserContainer>
  );
}

const StyledTeaserContainer = styled(Box)(({ theme }) => ({
  // Migrated from CMSTeasers.less (.scha__teasers)
  boxSizing: "border-box",
  "& .scha__teasers__group:nth-child(1n + 2)": {
    marginTop: "16px",
  },
  // Migrated from CMSTeasers.less (.scha__teasers__elements + modifiers)
  "& .scha__teasers__elements": {
    listStyle: "none",
    paddingLeft: 0,
    display: "flex",
    height: "100%",
    "&.scha__teasers__elements--DEFAULT": {
      // Local override of the :root --teaser-gap for the default grid.
      "--teaser-gap": "16px",
      [theme.breakpoints.up("tablet")]: {
        "--teaser-gap": "32px",
      },
      marginInline: "calc(var(--teaser-gap) / -2) !important",
      "& > *": {
        paddingInline: "calc(var(--teaser-gap) / 2) !important",
      },
    },
    "&.scha__teasers__elements--HORIZONTAL_SCROLL": {
      overflow: "auto",
      // .fullbleed mixin expanded
      width: "100vw",
      position: "relative",
      left: "50%",
      right: "50%",
      marginLeft: "-50vw",
      marginRight: "-50vw",
      padding: "0 16px",
      gap: "16px",
    },
  },
  "& .scha__teasers__element": {
    "& > *": {
      height: "100%",
    },
    // Mobile-only variant
    "&.scha__teasers__element--HORIZONTAL_SCROLL": {
      width: "238px",
      flex: "none",
    },
  },
  // Doubled class on purpose: must outrank swiper's own .swiper-slide height.
  "& .scha__teasers__swiper-slide.scha__teasers__swiper-slide": {
    height: "auto",
  },
  // Migrated from CMSTeasers.less (--VERTICAL variant). .scha__teaser is owned
  // by CMSTeaser.less until that file migrates; the descendant rules are kept
  // verbatim here.
  "&.scha__teasers--VERTICAL": {
    "& .scha__teasers__elements": {
      "& .scha__teaser": {
        marginBottom: "16px",
      },
      [theme.breakpoints.up("tablet")]: {
        display: "flex",
        justifyContent: "flex-start",
        flexFlow: "row wrap",
        "& .scha__teaser": {
          margin: "16px",
          marginBottom: 0,
        },
      },
    },
  },
  // Migrated from CMSTeasers.less (--HORIZONTAL variant). The LESS nested
  // `.scha__teasers__elements .scha__teasers__elements { min-width: 928px }`
  // rule was dead (no nested elements containers exist) and is dropped.
  // max-width: 768px preserved raw (theme down() would use 767.95px).
  "&.scha__teasers--HORIZONTAL": {
    "& .scha__teasers__group": {
      overflowX: "scroll",
    },
    "& .scha__teasers__elements": {
      display: "flex",
      justifyContent: "flex-start",
      flexFlow: "inherit",
      "& .scha__teaser": {
        margin: "16px",
        marginBottom: 0,
      },
      "@media (max-width: 768px)": {
        "& .scha__teaser": {
          width: "328px",
          flex: "1 0 328px",
          "&:first-child": {
            marginLeft: 0,
          },
          "&:last-child": {
            marginRight: 0,
          },
        },
      },
    },
    "& .scha__teasers__container--active": {
      position: "fixed",
      marginRight: "16px",
    },
  },
  // Original swiper pagination/navigation styles (predate the migration)
  "& .swiper": {
    paddingBottom: "24px", // 16px gap + 8px dot height
  },
  "& .swiper-pagination": {
    bottom: "0 !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
  },
  "& .swiper-pagination-bullet": {
    width: "8px",
    height: "8px",
    margin: "0 !important",
    borderRadius: "50%",
    opacity: 1,
    background: theme.palette.text.primary,
    transition: "background 0.2s",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  "& .swiper-pagination-bullet-active": {
    background: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
  "& .swiper-pagination-wrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  },
  "& .swiper-pagination-bullet-wrapper": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  "& .swiper-pagination-bullets.swiper-pagination-horizontal": {
    width: "auto",
  },
  "& .swiper-button": {
    color: theme.palette.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  "& .swiper-button-disabled": {
    opacity: "50%",
  },
}));
